import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, Calendar, Tag, LogOut, Briefcase, MapPin } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
export default function AdminDashboard() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [projectStats, setProjectStats] = useState({
        total: 0,
        published: 0,
        drafts: 0,
    });
    const { user, signOut } = useAuth();
    useEffect(() => {
        fetchProjects();
    }, []);
    const fetchProjects = async () => {
        try {
            const { data, error } = await supabase
                .from('projects')
                .select('*')
                .order('updated_at', { ascending: false });
            if (error)
                throw error;
            setProjects(data || []);
            const total = data?.length || 0;
            const published = data?.filter(project => project.status === 'published').length || 0;
            const drafts = data?.filter(project => project.status === 'draft').length || 0;
            setProjectStats({ total, published, drafts });
        }
        catch (error) {
            console.error('Error fetching projects:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const handleDeleteProject = async (id) => {
        if (!confirm('この施工実績を削除してもよろしいですか？'))
            return;
        try {
            const { error } = await supabase
                .from('projects')
                .delete()
                .eq('id', id);
            if (error)
                throw error;
            fetchProjects();
        }
        catch (error) {
            console.error('Error deleting project:', error);
            alert('施工実績の削除に失敗しました。');
        }
    };
    const handleLogout = async () => {
        await signOut();
    };
    if (loading) {
        return (<div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-navy'></div>
      </div>);
    }
    return (<div className='min-h-screen bg-gray-50'>
      {/* Header */}
      <header className='bg-white shadow-sm border-b'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-4'>
            <Link to='/' className='hover:opacity-80 transition-opacity'>
              <h1 className='text-2xl font-bebas tracking-wider text-navy'>
                MORIDEN SETSUBI
              </h1>
              <p className='text-sm text-gray-600'>コンテンツ管理システム</p>
            </Link>

            <div className='flex items-center space-x-4'>
              <span className='text-sm text-gray-600'>
                ようこそ、{user?.email}さん
              </span>
              <button onClick={handleLogout} className='inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'>
                <LogOut className='w-4 h-4 mr-2'/>
                ログアウト
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Stats Cards */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className='bg-white p-6 rounded-lg shadow-sm'>
            <div className='flex items-center'>
              <div className='p-2 bg-blue-100 rounded-lg'>
                <Briefcase className='w-6 h-6 text-blue-600'/>
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>総実績数</p>
                <p className='text-2xl font-bold text-gray-900'>{projectStats.total}</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className='bg-white p-6 rounded-lg shadow-sm'>
            <div className='flex items-center'>
              <div className='p-2 bg-green-100 rounded-lg'>
                <Eye className='w-6 h-6 text-green-600'/>
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>公開済み</p>
                <p className='text-2xl font-bold text-gray-900'>{projectStats.published}</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className='bg-white p-6 rounded-lg shadow-sm'>
            <div className='flex items-center'>
              <div className='p-2 bg-yellow-100 rounded-lg'>
                <Edit className='w-6 h-6 text-yellow-600'/>
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>下書き</p>
                <p className='text-2xl font-bold text-gray-900'>{projectStats.drafts}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Actions */}
        <div className='flex justify-between items-center mb-6'>
          <h2 className='text-xl font-semibold text-gray-900'>施工実績一覧</h2>
          <Link to='/admin/projects/new' className='inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-navy hover:bg-navy/90'>
            <Plus className='w-4 h-4 mr-2'/>
            新しい施工実績を作成
          </Link>
        </div>

        {/* Projects Table */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className='bg-white shadow-sm rounded-lg overflow-hidden'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  タイトル
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  カテゴリ
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  場所
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  ステータス
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  更新日
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  操作
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {projects.map((project) => (<tr key={project.id} className='hover:bg-gray-50'>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <div className='text-sm font-medium text-gray-900'>
                      {project.title}
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
                      <Tag className='w-3 h-3 mr-1'/>
                      {project.category}
                    </span>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <span className='inline-flex items-center text-sm text-gray-500'>
                      <MapPin className='w-4 h-4 mr-1'/>
                      {project.location}
                    </span>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${project.status === 'published'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'}`}>
                      {project.status === 'published' ? '公開済み' : '下書き'}
                    </span>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                    <div className='flex items-center'>
                      <Calendar className='w-4 h-4 mr-1'/>
                      {new Date(project.updated_at).toLocaleDateString('ja-JP')}
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                    <div className='flex space-x-2'>
                      {project.status === 'published' && (<Link to='/works' className='text-blue-600 hover:text-blue-900' title='実績を表示'>
                          <Eye className='w-4 h-4'/>
                        </Link>)}
                      <Link to={`/admin/projects/${project.id}/edit`} className='text-indigo-600 hover:text-indigo-900' title='編集'>
                        <Edit className='w-4 h-4'/>
                      </Link>
                      <button onClick={() => handleDeleteProject(project.id)} className='text-red-600 hover:text-red-900' title='削除'>
                        <Trash2 className='w-4 h-4'/>
                      </button>
                    </div>
                  </td>
                </tr>))}
            </tbody>
          </table>

          {projects.length === 0 && (<div className='text-center py-12'>
              <Briefcase className='mx-auto h-12 w-12 text-gray-400'/>
              <h3 className='mt-2 text-sm font-medium text-gray-900'>施工実績がありません</h3>
              <p className='mt-1 text-sm text-gray-500'>
                最初の施工実績を作成してみましょう。
              </p>
              <div className='mt-6'>
                <Link to='/admin/projects/new' className='inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-navy hover:bg-navy/90'>
                  <Plus className='w-4 h-4 mr-2'/>
                  新しい施工実績を作成
                </Link>
              </div>
            </div>)}
        </motion.div>
      </div>
    </div>);
}
